import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';

export const Gallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const galleryRef = useRef(null);
  const trackedRef = useRef(false);
  const { t } = useLanguage();

  const getImagesByCategory = (cat) =>
    cat === 'all' ? galleryImages : galleryImages.filter(img => img.category === cat);

  const images = getImagesByCategory(currentCategory);

  // Reset index when category changes
  useEffect(() => { setCurrentIndex(0); }, [currentCategory]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, currentIndex, images.length]);

  // GA tracking
  useEffect(() => {
    const node = galleryRef.current;
    if (!node || trackedRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !trackedRef.current) {
        trackedRef.current = true;
        if (typeof window.gtag === 'function') window.gtag('event', 'gallery_view');
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const next = useCallback(() => setCurrentIndex(i => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setCurrentIndex(i => (i - 1 + images.length) % images.length), [images.length]);

  const categories = [
    { value: 'all', label: t('gallery.allPhotos') },
    { value: 'exterior', label: t('gallery.exterior') },
    { value: 'living', label: t('gallery.living') },
    { value: 'kitchen', label: t('gallery.kitchen') },
    { value: 'dining', label: t('gallery.dining') },
    { value: 'outdoor', label: t('gallery.outdoor') },
    { value: 'bedroom', label: t('gallery.bedroom') },
    { value: 'bathroom', label: t('gallery.bathroom') },
    { value: 'antiques', label: t('gallery.antiques') },
    { value: 'entertainment', label: t('gallery.entertainment') },
  ];

  const current = images[currentIndex];

  return (
    <section className="gallery-section" ref={galleryRef}>
      <div className="container-custom">
        <div className="text-center mb-16">
          <div className="section-badge">{t('gallery.badge')}</div>
          <h2 className="section-title">{t('gallery.title')}</h2>
          <p className="section-description">{t('gallery.description')}</p>
        </div>

        {/* Category tabs */}
        <div className="gallery-tabs-list">
          {categories.map(cat => (
            <button
              key={cat.value}
              className={`gallery-tab${currentCategory === cat.value ? ' gallery-tab-active' : ''}`}
              onClick={() => setCurrentCategory(cat.value)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Main carousel */}
        <div className="gallery-carousel">
          {/* Main image */}
          <div className="gallery-carousel-main" onClick={() => setLightboxOpen(true)}>
            <img src={current.url} alt={current.title} />
            <div className="gallery-carousel-overlay">
              <span className="gallery-carousel-title">{current.title}</span>
            </div>
            <div className="gallery-carousel-counter">{currentIndex + 1} / {images.length}</div>

            <button
              className="gallery-carousel-btn gallery-carousel-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous"
            >
              <ChevronLeft />
            </button>
            <button
              className="gallery-carousel-btn gallery-carousel-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next"
            >
              <ChevronRight />
            </button>
          </div>

          {/* Thumbnails */}
          <div className="gallery-thumbnails">
            {images.map((img, i) => (
              <div
                key={i}
                className={`gallery-thumb${i === currentIndex ? ' gallery-thumb-active' : ''}`}
                onClick={() => setCurrentIndex(i)}
              >
                <img src={img.url} alt={img.title} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>
            <X className="h-8 w-8" />
          </button>
          <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }}>
            <ChevronLeft className="h-8 w-8" />
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={current.url} alt={current.title} />
            <div className="lightbox-info">
              <span className="lightbox-counter">{currentIndex + 1} / {images.length}</span>
              <h3 className="lightbox-title">{current.title}</h3>
            </div>
          </div>
          <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }}>
            <ChevronRight className="h-8 w-8" />
          </button>
        </div>
      )}
    </section>
  );
};
